import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

import { EXPLORA_DESTINATIONS, findToursForDestination } from "@/lib/explora-destinations";
import type { UnifiedTour } from "@/lib/catalog.functions";

import destTulum from "@/assets/dest-tulum.webp";
import destCozumel from "@/assets/dest-cozumel.webp";
import destCdmx from "@/assets/dest-cdmx.webp";
import destMerida from "@/assets/dest-merida.webp";
import cancunAsset from "@/assets/destinations/cancun.jpg.asset.json";
import vallartaAsset from "@/assets/destinations/puerto-vallarta.jpg.asset.json";

/** Orden fijo pedido. Las fotografías y los slugs ya existen en el proyecto. */
const PICKS: { slug: string; image: string }[] = [
  { slug: "cancun", image: cancunAsset.url },
  { slug: "puerto-vallarta", image: vallartaAsset.url },
  { slug: "tulum", image: destTulum },
  { slug: "cozumel", image: destCozumel },
  { slug: "cdmx", image: destCdmx },
  { slug: "merida", image: destMerida },
];

export function PopularDestinations({ tours }: { tours: UnifiedTour[] }) {
  const { t } = useTranslation();
  const items = useMemo(
    () =>
      PICKS.filter((p) => ["cancun", "tulum", "cozumel", "merida"].includes(p.slug)).map((p) => {
        const dest = EXPLORA_DESTINATIONS.find((d) => d.slug === p.slug);
        if (!dest) return null;
        return {
          slug: dest.slug,
          name: dest.name,
          image: p.image,
          count: findToursForDestination(dest, tours).length,
        };
      }).filter((x): x is { slug: string; name: string; image: string; count: number } => !!x && x.count > 0),
    [tours],
  );

  if (items.length === 0) return null;

  return (
    <section aria-label={t("homeV2.destEyebrow")} className="bg-white">
      <div className="mx-auto max-w-[1360px] px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
              <MapPin className="h-3.5 w-3.5" /> {t("homeV2.destEyebrow")}
            </span>
            <h2 className="mt-1.5 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {t("homeV2.destTitle")}
            </h2>
          </div>
          <Link
            to="/destinos"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            {t("homeV2.destAll")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <ul className="mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-4 md:overflow-visible md:pb-0">
          {items.map((d, i) => (
            <li key={d.slug} className="w-[78%] shrink-0 snap-start md:w-auto">
              <Link
                to="/tours/$city"
                params={{ city: d.slug }}
                className="group relative block aspect-[4/5] overflow-hidden rounded-[20px] ring-1 ring-border/60 transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-22px_rgba(23,23,23,0.45)]"
              >
                <img
                  src={d.image}
                  alt={d.name}
                  width={480}
                  height={600}
                  loading={i < 2 ? "eager" : "lazy"}
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <div className="font-display text-lg font-semibold leading-tight">{d.name}</div>
                  {d.count > 0 && (
                    <div className="text-[12px] text-white/80"
                      suppressHydrationWarning>
                      {d.count === 1
                        ? t("homeV2.expOne", { count: d.count })
                        : t("homeV2.expMany", { count: d.count })}
                    </div>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
